/**
 * Always Hint Row — compact tile: image (with tooltip) + optional kind badge +
 * item drop slot. Click the image to remove the always hint.
 */
import { ArrowRight, X } from 'lucide-react';
import { useTracker, type OotItemDto } from '@/contexts/tracker-context';
import { ItemDropSlot } from './item-drop-slot';
import type { OotItemData } from './data/oot-items-data';
import { ALWAYS_KIND_BADGE, type AlwaysHintData } from './data/always-hints-data';

interface AlwaysRowProps {
  hint: AlwaysHintData;
}

export function AlwaysRow({ hint }: AlwaysRowProps) {
  const { state, dispatch } = useTracker();
  const item = state.alwaysItems[hint.name] ?? null;
  // Dual hints store the second item under a suffixed key in the same map.
  const item2Key = `${hint.name}__2`;
  const item2 = state.alwaysItems[item2Key] ?? null;
  const badge = ALWAYS_KIND_BADGE[hint.kind];

  const handleItemChange = (next: OotItemData | null) => {
    dispatch({
      type: 'SET_ALWAYS_ITEM',
      hintName: hint.name,
      item: next as unknown as OotItemDto | null,
    });
  };

  const handleItem2Change = (next: OotItemData | null) => {
    dispatch({
      type: 'SET_ALWAYS_ITEM',
      hintName: item2Key,
      item: next as unknown as OotItemDto | null,
    });
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({ type: 'REMOVE_ALWAYS', hintName: hint.name });
  };

  return (
    <div
      className="group relative flex items-center gap-1 py-0.5 px-1.5 bg-gray-800/50 rounded-md"
      title={hint.name}
    >
      <button
        onContextMenu={handleRemove}
        onClick={handleRemove}
        title={`${hint.name} (click to remove)`}
        className="relative flex items-center flex-shrink-0 hover:brightness-150 transition-all"
      >
        {hint.img ? (
          <img src={hint.img} alt="" className="h-7 w-7 object-contain" />
        ) : (
          <span className="h-7 w-7 inline-flex items-center justify-center text-[9px] text-gray-300 bg-gray-700 rounded">
            {hint.name.slice(0, 4)}
          </span>
        )}
        {hint.img2 && (
          <img src={hint.img2} alt="" className="h-7 w-7 object-contain -ml-1" />
        )}
        <X
          size={10}
          className="absolute -top-0.5 -right-0.5 text-red-400 bg-gray-900/70 rounded opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </button>
      {badge.label && (
        <span
          className={`text-[9px] font-bold leading-none px-1 py-0.5 rounded ${badge.color}`}
          title={hint.kind}
        >
          {badge.label}
        </span>
      )}
      <ArrowRight size={12} className="text-gray-500 flex-shrink-0" />
      <ItemDropSlot
        item={item as unknown as OotItemData | null}
        onItemChange={handleItemChange}
      />
      {hint.kind === 'dual' && (
        <ItemDropSlot
          item={item2 as unknown as OotItemData | null}
          onItemChange={handleItem2Change}
        />
      )}
    </div>
  );
}
